import React, { useCallback, useRef, useState } from 'react';
import TDK from 'components/tdk';
import Layout from 'components/layout';
import Contact from 'components/contact';
import cn from 'classnames';
import MultipleBanner from 'components/product/banner/multiple';
import UseResize from 'utils/hooks/useResize';
import useElementInViewport, { clearTimer } from 'utils/hooks/useScrollPlay';
import type { TimerRef } from 'utils/hooks/useScrollPlay';
import Transition from 'components/transition';
import { Tabs } from 'antd';
import { arrowIcon } from '../../components/solution/skeletonNew/index';
import { arrowIcon as arrowIcon2 } from 'constants/platform';
import { usePagination } from '../../utils/hooks/usePagination';
import { scenes, advantages, dockingWays, cases, PAGESIZE, firstPageIndex, urlPre } from 'constants/sdk_api';
import style from './index.module.scss';
import TabPane from 'components/tabPane';

const Index = () => {
  const { width: clientWidth } = UseResize();
  const logoContainer = useRef<HTMLDivElement>(null);
  const stopLogoAct = useRef(false);
  const timerRef = useRef<TimerRef>(null);
  const [mobileActiveKey, setMobileActiveKey] = useState(0);
  const { currentCases, currentPage, totalPages, activeKey, handleNextPage, handlePrevPage, setActiveKey } = usePagination(cases, PAGESIZE);

  // 切换页码
  const handlePageChange = (newPage: number) => {
    currentPage > newPage ? handlePrevPage() : handleNextPage();
  };

  // logo轮播
  const handleLogoPlay = useCallback(() => {
    clearTimer(timerRef);
    if (stopLogoAct.current) return;

    timerRef.current = setInterval(() => {
      clientWidth < 768 ? setMobileActiveKey(mobileActiveKey => (mobileActiveKey + 1) % cases.length) : setActiveKey(activeKey => activeKey + 1);
    }, 3000);

    if (currentPage === totalPages && activeKey === PAGESIZE) {
      handlePrevPage();
      return;
    }

    if (activeKey + 1 > PAGESIZE) {
      handleNextPage();
      return;
    }
  }, [activeKey, currentPage, clientWidth, totalPages, handleNextPage, handlePrevPage, setActiveKey]);

  // 进入视口，自动轮播
  useElementInViewport({ targetElement: logoContainer, onEnterViewport: handleLogoPlay, onStayInViewport: handleLogoPlay });

  // 自定义tabBar
  const CustomTabBar: React.FC<any> = props => {
    const { activeKey, onTabClick, panes } = props;

    return (
      <div className={style.tabBarWrapper}>
        <div className={style.customTabBar}>
          <div className={cn(style.tabArrow, style.left, currentPage === firstPageIndex && style.hide)} onClick={handlePrevPage} />
          {panes &&
            panes.map((item: any) => (
              <div key={item.key} className={cn(style.customTab, activeKey === item.key && style.active)} onClick={() => onTabClick(item.key)}>
                <span>{item.props.tab}</span>
              </div>
            ))}
          <div className={cn(style.tabArrow, style.right, currentPage === totalPages && style.hide)} onClick={handleNextPage} />
        </div>
        <div className={style.direction}>
          {Array.from({ length: totalPages }).map((_, index) => (
            <span className={cn(style.circle, currentPage === index + 1 && style.active)} key={index} onClick={() => handlePageChange(index + 1)}></span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Layout layoutClass={style.layout} headerClass={style.header}>
      <TDK pageKey="api" />
      <div className={style.container}>
        {/* 头部 */}
        <MultipleBanner
          type={2}
          mUrl={urlPre + 'headerM.jpg'}
          pcUrl={urlPre + 'header.jpg'}
          title={'泛业务开发底座'}
          desc={`通过全平台SDK及API接口，嵌入各类业务系统，为政务、政法、央国企、金融、能源、应急等全行业\n提供高品质、稳定、安全的互动音视频服务，以全场景应用生态快速激活千行百业数字化转型升级。`}
          mDesc={`通过全平台SDK及API接口，嵌入各类业务系统，\n为政务、政法、央国企、金融、能源、应急等\n全行业提供高品质、稳定、安全的互动\n音视频服务，以全场景应用生态快速激活千行百业\n数字化转型升级。`}
          className={cn(style.headerBanner)}
          delay={0.5}
          animate={'fadeIn'}
        ></MultipleBanner>
        {/* 多维场景，广泛应用 */}
        <div className={style.scenes}>
          <Transition delay={0.5}>
            <p className={style.title}>多维场景，广泛应用</p>
          </Transition>
          <Transition delay={0.8}>
            <div className={style.content}>
              {scenes.map((item: any, index: number) => {
                return (
                  <div key={item.title} className={style.item}>
                    <div className={style.left}>
                      <p className={style.itemTitle}>{item.title}</p>
                      <p className={style.itemDesc}>{item.desc}</p>
                      <img className={style.arrowIcon} src={arrowIcon[1]} alt="" />
                    </div>
                    <img className={style.itemImg} src={urlPre + `scene${index}.png`} alt={item.title} />
                  </div>
                );
              })}
            </div>
          </Transition>
        </div>
        {/* 开放集成 ，大有作为 */}
        <div className={style.advantages}>
          <Transition delay={0.5}>
            <p className={style.title}>开放集成 ，大有作为</p>
          </Transition>
          <div className={style.content}>
            {advantages.map((item: any) => {
              return (
                <div className={style.item} key={item.title}>
                  <Transition delay={0.8}>
                    <div className={style.left}>
                      <div className={style.itemTitle}>{item.title}</div>
                      <div className={cn(style.itemDesc)}>
                        {item.desc.map((item: any, index: number) => {
                          return (
                            <p className={style.dot} key={index}>
                              {item}
                            </p>
                          );
                        })}
                      </div>
                    </div>
                  </Transition>
                  <Transition delay={0.8}>
                    <img className={style.icon} src={item.icon} alt="" />
                  </Transition>
                </div>
              );
            })}
          </div>
        </div>
        {/* 多种对接方式 */}
        <div className={style.dockingWays}>
          <Transition delay={0.5}>
            <div className={style.title}>多种对接方式</div>
          </Transition>
          <Transition delay={0.5}>
            <div className={style.content}>
              {dockingWays.map((item: any) => (
                <div className={style.item} key={item.title}>
                  <img className={style.icon} src={item.icon} alt="" />
                  <div className={style.itemTitle}>{item.title}</div>
                  <div className={style.itemDesc}>{item.desc}</div>
                </div>
              ))}
            </div>
          </Transition>
        </div>
        {/* 一路相伴 */}
        <div className={style.clients}>
          <img className={style.arrow} src={arrowIcon2[4]} alt="" />
          <Transition delay={0.5}>
            <p className={style.mainTitle}>典型案例</p>
          </Transition>

          <Transition delay={0.8}>
            <div ref={logoContainer}>
              {clientWidth < 768 ? (
                <Tabs
                  className={style.client}
                  activeKey={mobileActiveKey.toString()}
                  onTabClick={(mobileActiveKey: string) => {
                    stopLogoAct.current = true;
                    setMobileActiveKey(Number(mobileActiveKey));
                    clearTimer(timerRef);
                  }}
                >
                  {TabPane({ list: cases, activeKey: mobileActiveKey })}
                </Tabs>
              ) : (
                <Tabs
                  className={style.client}
                  activeKey={activeKey.toString()}
                  renderTabBar={props => <CustomTabBar {...props} onPrevClick={handlePrevPage} onNextClick={handleNextPage} />}
                  onTabClick={(activeKey: string) => {
                    stopLogoAct.current = true;
                    setActiveKey(Number(activeKey));
                    clearTimer(timerRef);
                  }}
                >
                  {TabPane({ list: currentCases, activeKey })}
                </Tabs>
              )}
            </div>
          </Transition>
          <Transition delay={0.9}>
            <Contact className={cn(style['contact-footer'])} btnType={'newBtn'} />
          </Transition>
        </div>
      </div>
    </Layout>
  );
};

export default Index;
